// 8 方位のリサイズハンドル用ドラッグハンドラ。
//
// 振る舞い:
// - pointerdown でドラッグ開始（基準位置を捕捉）
// - pointermove でステージ座標差分を方位に応じて x/y/w/h に反映し、対象 DOM を
//   即時プレビュー（doc store/サーバへは送らない）
// - pointerup で最終 x/y/w/h を transformObject op として送信し永続化
//
// 簡略化:
// - rotation 中の object ではハンドル表示は object と同じ角度へ追従させるが、
//   リサイズ計算は world 軸基準で行う（object-local 軸のリサイズは非対応）
// - 最小 16px 制限（潰れ防止）
// - Shift キーでアスペクト比固定はサポートしない（v1 仕様）

import { useEffect, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

export type ResizeDirection =
  | "nw"
  | "n"
  | "ne"
  | "e"
  | "se"
  | "s"
  | "sw"
  | "w";

interface Options {
  readonly direction: ResizeDirection;
  readonly handleRef: RefObject<HTMLElement | null>;
  readonly objectId: string;
  readonly dispatcher: CanvasOpDispatcher;
  readonly initialX: number;
  readonly initialY: number;
  readonly initialW: number;
  readonly initialH: number;
  /** 対象オブジェクトの DOM 要素（プレビュー用に left/top/width/height を直接書き換える）。 */
  readonly targetRef: RefObject<HTMLElement | null>;
}

const MIN_SIZE_PX = 16;

export function useResizeHandle(options: Options): void {
  const {
    direction,
    handleRef,
    objectId,
    dispatcher,
    initialX,
    initialY,
    initialW,
    initialH,
    targetRef,
  } = options;

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return undefined;

    let dragging:
      | {
          startClientX: number;
          startClientY: number;
        }
      | null = null;

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return;
      dragging = { startClientX: event.clientX, startClientY: event.clientY };
      handle.setPointerCapture(event.pointerId);
      // 親オブジェクトの drag や stage の pan を起こさせない
      event.stopPropagation();
    };

    const computeResized = (
      clientX: number,
      clientY: number
    ): { x: number; y: number; w: number; h: number } => {
      const view = useCanvasStore.getState().view;
      const dxStage = (clientX - (dragging?.startClientX ?? clientX)) / view.zoom;
      const dyStage = (clientY - (dragging?.startClientY ?? clientY)) / view.zoom;

      let nextX = initialX;
      let nextY = initialY;
      let nextW = initialW;
      let nextH = initialH;

      // 方位ごとに左/右/上/下のどれを動かすかを決める
      const movesLeft = direction === "nw" || direction === "w" || direction === "sw";
      const movesRight = direction === "ne" || direction === "e" || direction === "se";
      const movesTop = direction === "nw" || direction === "n" || direction === "ne";
      const movesBottom = direction === "sw" || direction === "s" || direction === "se";

      if (movesLeft) {
        nextX = initialX + dxStage;
        nextW = initialW - dxStage;
        if (nextW < MIN_SIZE_PX) {
          nextX = initialX + (initialW - MIN_SIZE_PX);
          nextW = MIN_SIZE_PX;
        }
      } else if (movesRight) {
        nextW = initialW + dxStage;
        if (nextW < MIN_SIZE_PX) nextW = MIN_SIZE_PX;
      }

      if (movesTop) {
        nextY = initialY + dyStage;
        nextH = initialH - dyStage;
        if (nextH < MIN_SIZE_PX) {
          nextY = initialY + (initialH - MIN_SIZE_PX);
          nextH = MIN_SIZE_PX;
        }
      } else if (movesBottom) {
        nextH = initialH + dyStage;
        if (nextH < MIN_SIZE_PX) nextH = MIN_SIZE_PX;
      }

      return { x: nextX, y: nextY, w: nextW, h: nextH };
    };

    const onPointerMove = (event: PointerEvent): void => {
      if (!dragging) return;
      const next = computeResized(event.clientX, event.clientY);
      const target = targetRef.current;
      if (target) {
        target.style.left = `${next.x}px`;
        target.style.top = `${next.y}px`;
        target.style.width = `${next.w}px`;
        target.style.height = `${next.h}px`;
      }
      updateSelectionBoxPreview(next);
    };

    const onPointerUp = (event: PointerEvent): void => {
      if (!dragging) return;
      const next = computeResized(event.clientX, event.clientY);
      dragging = null;
      if (handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }
      dispatcher.transformObject(objectId, {
        x: next.x,
        y: next.y,
        w: next.w,
        h: next.h,
      });
    };

    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerUp);
    return () => {
      handle.removeEventListener("pointerdown", onPointerDown);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerUp);
    };
  }, [direction, handleRef, objectId, dispatcher, initialX, initialY, initialW, initialH, targetRef]);
}

function updateSelectionBoxPreview(next: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }): void {
  const box = document.querySelector<HTMLElement>('[data-testid="canvas-selection-box"]');
  if (!box) return;
  box.style.left = `${next.x}px`;
  box.style.top = `${next.y}px`;
  box.style.width = `${next.w}px`;
  box.style.height = `${next.h}px`;
}
